import React, { Children, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import classnames from 'classnames';

import { Card } from 'components/core/Card';
import { ImageBadge } from 'components/core/ImageBadge';
import { FacilityCard, TopYielderCard } from 'components/search/FacilityCard';
import { AdditionalFacilitiesIndicator } from 'components/search/AdditionalFacilitiesIndicator';
import priceTag from 'assets/images/icons/icon-price-tag.svg';
import EmptyFacilityList from './EmptyFacilityList';

import './facilityList.scss';
import { TrackingClient } from '@sparefoot/tracking-client';

// Injects components such as PromoCards between the FacilityCards
export const withInjectedContent = (WrappedComponent) => {
	const WithInjectedContent = (props) => {
		const {
			firstListingIsVipEligible,
			vipAccount,
			vipFacilityId,
			vipFacilityImages,
			vipShowMapHandler,
			injectedComponents,
			injectAllEvenIfNoContent,
			facilities,
			page,
			openModal,
			phone,
			onFacilityMouseOver,
			onFacilityMouseOut,
			searchType,
			getLocalPhoneNumberByZipCode,
			getLocalPhoneNumberByCityState,
			handleFacilityCardVisible,
			isStorageDealsPage,
			fmfFacilitiesCount,
			promoFacilitiesCount,
			facilitiesPerPage,
			location,
			isCLP,
			baseUrl,
			fullWidthCards,
			cityDrilldown,
		} = props;

		const derivedProps = useMemo(() => {
			const isFirstPage = page === 1 || !page;

			const facilityCardsWithInjectedComponents = facilities.reduce(
				(components, facility, index) => {
					// If we should inject a component at this index
					if (
						isFirstPage &&
						injectedComponents &&
						injectedComponents[index]
					) {
						components.push(injectedComponents[index]);
					}

					// Insert the normal facility card and the top yielder card
					switch (get(facility, 'cardType')) {
						case 'topYielderCard':
							components.push(
								<TopYielderCard
									facility={facility}
									onVisible={handleFacilityCardVisible}
									position={index + 1}
								/>,
							);
							break;
						default:
							components.push(
								<Card
									className={facility.isHidden && 'noPadding'}
								>
									<FacilityCard
										key={facility.id}
										searchLocation={location}
										facility={facility}
										openModal={openModal}
										phone={phone}
										onMouseEnter={onFacilityMouseOver}
										onMouseLeave={onFacilityMouseOut}
										searchType={searchType}
										getLocalPhoneNumberByZipCode={
											getLocalPhoneNumberByZipCode
										}
										getLocalPhoneNumberByCityState={
											getLocalPhoneNumberByCityState
										}
										position={index + 1}
										onVisible={handleFacilityCardVisible}
										isVipFacility={
											facility.id === vipFacilityId &&
											firstListingIsVipEligible
										}
										vipAccount={vipAccount}
										vipFacilityImages={vipFacilityImages}
										vipShowMapHandler={vipShowMapHandler}
										imageBadge={
											isStorageDealsPage &&
											facility.hasFmf && (
												<ImageBadge
													icon={priceTag}
													color="green"
													isFacilityCard
												/>
											)
										}
										baseUrl={baseUrl}
										withSchema={isCLP}
										fullWidthCard={fullWidthCards}
										cityDrilldown={cityDrilldown}
									/>
								</Card>,
							);
					}

					// FMF Divider
					if (
						index === fmfFacilitiesCount - 1 &&
						fmfFacilitiesCount !== promoFacilitiesCount &&
						fmfFacilitiesCount !== facilitiesPerPage
					) {
						// Don't show divider if all items are promo
						components.push(
							<AdditionalFacilitiesIndicator
								nearbyTypeName="deals"
								location={location}
							/>,
						);
					}

					// Other promos Divider
					if (
						index === promoFacilitiesCount - 1 &&
						promoFacilitiesCount !== facilitiesPerPage
					) {
						// Don't show divider if all items are promo
						components.push(
							<AdditionalFacilitiesIndicator
								location={location}
							/>,
						);
					}

					return components;
				},
				[],
			);

			// Inject any leftover components if there were not enough facilities to iterate over
			if (isFirstPage && injectedComponents && injectAllEvenIfNoContent) {
				const numFacilities = facilities.length;
				Object.keys(injectedComponents)
					.filter((keyIndex) => parseInt(keyIndex) >= numFacilities)
					.sort((a, b) => a - b)
					.forEach((keyIndex) => {
						facilityCardsWithInjectedComponents.push(
							injectedComponents[keyIndex],
						);
					});
			}

			return { facilityCardsWithInjectedComponents };
		}, [
			injectedComponents,
			injectAllEvenIfNoContent,
			facilities,
			page,
			searchType,
			cityDrilldown,
		]); // eslint-disable-line react-hooks/exhaustive-deps

		return (
			<WrappedComponent
				{...props}
				{...derivedProps}
			/>
		);
	};
	return WithInjectedContent;
};

const withTracking = (WrappedComponent) => {
	const WithTracking = (props) => {
		const { facilities, searchTerm } = props;

		useEffect(() => {
			let facilityIds = [];

			let formattedFacilities = facilities?.map((facility) => {
				facilityIds.push(facility?.id);

				return {
					id: facility?.id,
					name: facility?.name,
					location: facility?.location?.id,
					facility_rank: facility?.facilityRank || '',
				};
			});

			TrackingClient.trackFacilityListViewed({
				facilities: formattedFacilities,
				facilities_list: facilityIds,
				search_term: searchTerm,
			});
		});

		return <WrappedComponent {...props} />;
	};
	return WithTracking;
};

export const enhance = (Component) =>
	withInjectedContent(withTracking(withInjectedContent(Component)));

export function FacilityList({
	facilities = [],
	showNoResults,
	listType = 'column',
	narrow,
	facilityCardsWithInjectedComponents,
	getTollfreePhoneNumber,
	fullWidthCards,
}) {
	return (
		<div
			className={classnames(
				'facility-list',
				listType,
				{ narrow },
				{ fullWidthCards },
			)}
		>
			{!facilities.length && showNoResults && (
				<EmptyFacilityList
					getTollfreePhoneNumber={getTollfreePhoneNumber}
				/>
			)}
			{Children.toArray(
				facilityCardsWithInjectedComponents.map((child) => (
					<div className="facility-list-card">{child}</div>
				)),
			)}
		</div>
	);
}

FacilityList.propTypes = {
	facilities: PropTypes.arrayOf(PropTypes.object),
	facilityCardsWithInjectedComponents: PropTypes.arrayOf(PropTypes.node),
	listType: PropTypes.oneOf(['grid', 'column']),
	narrow: PropTypes.bool,
	showNoResults: PropTypes.bool,
	getTollfreePhoneNumber: PropTypes.func.isRequired,
	fullWidthCards: PropTypes.bool,
};

export default enhance(FacilityList);
